import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export interface TenantConfig {
  id: string;
  name: string;
  slug: string;
  logo_url: string;
  primary_color: string;
  accent_color: string;
  plan: string;
  features_enabled: string[];
  max_users: number;
  max_vehicles: number;
  open_trips_enabled?: boolean;
}

const DEFAULT_TENANT: TenantConfig = {
  id: '',
  name: 'TravelEase.AI',
  slug: '',
  logo_url: '',
  primary_color: '#152850',
  accent_color: '#059669',
  plan: 'professional',
  features_enabled: [],
  max_users: 50,
  max_vehicles: 30,
  open_trips_enabled: false };

interface TenantContextType {
  tenant: TenantConfig;
  loading: boolean;
  fetchTenant: () => Promise<void>;
  hasFeature: (feature: string) => boolean;
}

const TenantContext = createContext<TenantContextType>({
  tenant: DEFAULT_TENANT,
  loading: false,
  fetchTenant: async () => {},
  hasFeature: () => true });

export const useTenant = () => useContext(TenantContext);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantConfig>(DEFAULT_TENANT);
  const [loading, setLoading] = useState(false);

  const fetchTenant = useCallback(async () => {
    if (!localStorage.getItem('user')) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/tenants/me`);
      if (data.tenant) {
        setTenant({ ...DEFAULT_TENANT, ...data.tenant });
        applyBranding(data.tenant);
      }
    } catch {
      // Not logged in or tenant not found — use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount if token exists, and listen for login events
  useEffect(() => {
    if (localStorage.getItem('user')) {
      fetchTenant();
    }
    const onLogin = () => fetchTenant();
    window.addEventListener('tenant-refresh', onLogin);
    return () => window.removeEventListener('tenant-refresh', onLogin);
  }, [fetchTenant]);

  const hasFeature = useCallback(
    (feature: string) => {
      // If no features configured, allow all (backwards compat)
      if (!tenant.features_enabled || tenant.features_enabled.length === 0) return true;
      return tenant.features_enabled.includes(feature);
    },
    [tenant.features_enabled]
  );

  return (
    <TenantContext.Provider value={{ tenant, loading, fetchTenant, hasFeature }}>
      {children}
    </TenantContext.Provider>
  );
}

/** Apply tenant branding as CSS custom properties */
function applyBranding(t: Partial<TenantConfig>) {
  const root = document.documentElement;
  if (t.primary_color) {
    root.style.setProperty('--tenant-primary', t.primary_color);
    // Convert hex to HSL for shadcn compatibility
    const hsl = hexToHSL(t.primary_color);
    if (hsl) root.style.setProperty('--tenant-primary-hsl', hsl);
  }
  if (t.accent_color) {
    root.style.setProperty('--tenant-accent', t.accent_color);
    const hsl = hexToHSL(t.accent_color);
    if (hsl) root.style.setProperty('--tenant-accent-hsl', hsl);
  }
}

function hexToHSL(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
